/**
 * FreedrIQScoreCard — compact card that shows a user's cumulative FreezeDryIQ
 * Score, confidence baseline, and category breakdown. Used on both the
 * Dashboard (own score) and MemberProfile (public read of another user).
 *
 * Props:
 *   userId  — the user whose summary to fetch. Omit for the current user.
 *   compact — when true, hide the category breakdown (used in tight rails).
 *   showCta — when true, render the "Take FreedrIQ" CTA for users who haven't
 *             yet completed a run. Default true when viewing your own card.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Award, Sparkles, TrendingUp, GraduationCap } from 'lucide-react';

const band = (s) => {
  if (s >= 85) return { label: 'Expert',          color: 'text-primary' };
  if (s >= 70) return { label: 'Practitioner',    color: 'text-primary' };
  if (s >= 50) return { label: 'Emerging',        color: 'text-amber-600' };
  if (s >= 25) return { label: 'Curious',         color: 'text-amber-600' };
  return          { label: 'Fresh Start',         color: 'text-emerald-600' };
};

const FreedrIQScoreCard = ({ userId, compact = false, showCta, tier = 'free' }) => {
  const { API, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const isSelf = !userId || userId === user?.id;
  const effectiveShowCta = showCta === undefined ? isSelf : showCta;

  useEffect(() => {
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    if (tier)   params.append('tier', tier);
    const url = `${API}/freedriq/summary${params.toString() ? `?${params.toString()}` : ''}`;
    axios.get(url)
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [API, userId, tier]);

  if (loading) {
    return (
      <div className="pro-card p-5" data-testid="freedriq-score-card-loading">
        <div className="h-3 w-24 bg-muted rounded animate-pulse" />
        <div className="h-9 w-20 bg-muted rounded mt-3 animate-pulse" />
      </div>
    );
  }

  if (!summary || !summary.has_taken) {
    if (!effectiveShowCta) return null;
    return (
      <div
        className="pro-card p-5 border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent"
        data-testid="freedriq-score-card-empty"
      >
        <div className="eyebrow text-primary inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> FreezeDryIQ Score
        </div>
        <h3 className="font-heading text-lg font-extrabold mt-1.5">Set your baseline</h3>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          Take the free 32-question FreedrIQ entry assessment — Prof. FreedrIQ uses it to
          personalise your learning path.
        </p>
        <Link
          to="/freedriq?tier=free&phase=pre"
          className="btn-primary inline-flex items-center gap-1.5 rounded-full font-bold text-xs px-4 py-2 mt-3"
          data-testid="freedriq-cta-take"
        >
          Take FreedrIQ <Sparkles className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  const b = band(summary.latest_score ?? 0);
  const conf = summary.latest_confidence ?? 0;
  const growth = summary.confidence_growth;
  const cats = Object.entries(summary.category_breakdown || {}).sort((a, b2) => b2[1].percent - a[1].percent);
  const byTier = summary.by_tier || [];
  const preScores = byTier.filter((r) => r.phase === 'pre');

  return (
    <div className="pro-card p-5" data-testid="freedriq-score-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <Award className="w-3 h-3" /> FreezeDryIQ Score
            {summary.tier && (
              <span className="ml-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                {summary.tier}
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <div className={`font-heading text-4xl font-extrabold ${b.color}`} data-testid="freedriq-score-card-score">
              {summary.latest_score}
            </div>
            <div className="text-xs text-muted-foreground font-bold uppercase tracking-widest">/ 100</div>
          </div>
          <div className="text-xs font-heading font-extrabold mt-0.5">{b.label}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Confidence</div>
          <div className="font-heading text-2xl font-extrabold" data-testid="freedriq-score-card-confidence">
            {conf}
          </div>
          {growth !== null && growth !== undefined && (
            <div className={`text-[11px] font-bold inline-flex items-center gap-1 ${growth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              <TrendingUp className="w-3 h-3" />
              {growth >= 0 ? '+' : ''}{growth} growth
            </div>
          )}
        </div>
      </div>

      {/* Per-tier scores when the learner has taken more than one */}
      {preScores.length > 1 && (
        <div className="mt-3 grid grid-cols-2 gap-2" data-testid="freedriq-score-card-by-tier">
          {preScores.slice(0, 4).map((r) => (
            <div key={`${r.tier}:${r.phase}`} className="rounded-lg border border-border p-2">
              <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">{r.tier}</div>
              <div className="font-heading text-lg font-extrabold">{r.score}</div>
              <div className="text-[10px] text-muted-foreground">conf {r.confidence ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      {!compact && cats.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5 inline-flex items-center gap-1">
            <GraduationCap className="w-3 h-3" /> Mastery by category
          </div>
          <div className="space-y-1.5">
            {cats.slice(0, 4).map(([cat, data]) => (
              <div key={cat}>
                <div className="flex justify-between text-[11px] mb-0.5">
                  <span className="font-semibold truncate">{cat}</span>
                  <span className="text-muted-foreground">{data.percent}%</span>
                </div>
                <div className="h-1 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${data.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isSelf && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">
            {summary.assessments_taken} assessment{summary.assessments_taken === 1 ? '' : 's'} taken
          </span>
          <Link
            to="/freedriq?tier=free&phase=pre"
            className="text-xs text-primary font-bold hover:underline inline-flex items-center gap-1"
            data-testid="freedriq-score-card-retake"
          >
            Retake <Sparkles className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default FreedrIQScoreCard;
